<script setup lang="ts">
import type { PropertyFilters } from '~/core/entities/property/filters';

import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { ORDER_BY, SHORTS_SORT_BY } from '~/core/entities/property/filters';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { dehyphenateString } from '~~/utils';

const props = defineProps<{
	infoLabel?: string | null;
	/**
	 * Filtros reativos da listagem (`useManager`). Incluem cidade/UF/bairro após a API
	 * aplicar `locationSlugFilters` — necessário porque `getFiltersFromPath` só lê a URL
	 * e não preenche `cities`/`states` para slugs como `sao-paulo-sp-brasil`.
	 */
	sessionFilters?: PropertyFilters;
}>();

const { ft_shorts_access_points } = useFeatureFlags();
const route = useRoute();

const pathFilters = computed(() =>
	PropertyFiltersPresenter.getFiltersFromPath(
		route.fullPath,
		undefined,
		'imoveis'
	)
);

const listingFilters = computed(
	() => props.sessionFilters ?? pathFilters.value
);

const addressSlug = computed(() => {
	const addressSlug = route.query.addressSlug;

	if (Array.isArray(addressSlug)) {
		return addressSlug.find(Boolean) || listingFilters.value.addressSlug || '';
	}

	return addressSlug || listingFilters.value.addressSlug || '';
});

const infoLabelFallback = computed(() => {
	const normalizedInfoLabel = props.infoLabel?.replace(/^em\s+/i, '').trim();

	if (normalizedInfoLabel) {
		return normalizedInfoLabel;
	}

	const locationSlug = listingFilters.value.locationSlug;

	if (!locationSlug) {
		return '';
	}

	const locationParts = locationSlug.split('-');

	if (locationParts.at(-1) !== 'brasil' || locationParts.length < 3) {
		return '';
	}

	const state = locationParts.at(-2)?.toUpperCase();
	const citySlug = locationParts.slice(0, -2).join('-');

	if (!citySlug || !state) {
		return '';
	}

	return `${dehyphenateString(citySlug)} - ${state}`;
});

const titleLocation = computed(() => {
	if (addressSlug.value) {
		const firstLocation = addressSlug.value?.split(',')[0]?.trim();
		return `${firstLocation} e arredores`;
	}

	if ((listingFilters.value.regions?.length || 0) > 1) {
		return 'sua busca';
	}

	return (
		listingFilters.value.regions?.[0] ||
		listingFilters.value.cities?.[0] ||
		listingFilters.value.macroregions?.[0] ||
		listingFilters.value.states?.[0] ||
		infoLabelFallback.value
	);
});

const title = computed(() =>
	titleLocation.value
		? `Explore ${titleLocation.value} em vídeos`
		: 'Descubra imóveis em vídeo'
);

const shortsFilters = computed(() => ({
	regions: listingFilters.value.regions,
	cities: listingFilters.value.cities,
	states: listingFilters.value.states,
	macroregions: listingFilters.value.macroregions,
	propertyTypes: listingFilters.value.propertyTypes,
	minAskingPrice: listingFilters.value.minAskingPrice,
	maxAskingPrice: listingFilters.value.maxAskingPrice,
	poiLocationLat: listingFilters.value.poiLocationLat,
	poiLocationLng: listingFilters.value.poiLocationLng,
	sortBy: SHORTS_SORT_BY.SUCCESS_PROBABILITY,
	orderBy: ORDER_BY.DESC
}));
</script>

<template>
	<SectionsShortsSection
		v-if="ft_shorts_access_points"
		:title="title"
		:filters="shortsFilters"
		variant="light"
		single-card-on-lg
		use-compact-mobile-top-spacing
		use-parent-mobile-gutter
		hide-bottom-border
		:show-title-icon="false"
		slider-section-name="shorts"
		video-card-section-name="shorts"
		cta-section-name="shorts"
	/>
</template>
